import React, { Suspense, useEffect } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import LoadingScreen from "../components/LoadingScreen";
import { NotebookList } from "../components/NotebookList";
import { NotebookInterface } from "../components/NotebookInterface";
import { MindMapBuilder } from "../components/MindMapBuilder";
import VisualQueryBuilder from "../components/VisualQueryBuilder";
import Button from "../components/ui/Button";
import {
  Save,
  PlayCircle,
  Database,
  Download,
  Share2,
  Plus,
} from "lucide-react";
import NLPQueryInput from "../components/NLPQueryInput";
import DatabaseTables from "../components/DatabaseTables";
import { Content } from "openai/resources/containers/files/content.mjs";

// Lazy imports
const QueryEditor = React.lazy(() => import("../components/QueryEditor"));
const QueryHistoryTabs = React.lazy(
  () => import("../components/QueryHistoryTabs")
);
const QueryPerformancePanel = React.lazy(
  () => import("../components/QueryPerformancePanel")
);
const ConnectionDetails = React.lazy(
  () => import("../components/ConnectionDetails")
);
const DashboardGrid = React.lazy(() => import("../components/DashboardGrid"));
const AdminPanel = React.lazy(() => import("../components/AdminPanel"));
const ConnectionManager = React.lazy(
  () => import("../components/ConnectionManager")
);
const LandingPage = React.lazy(() => import("../components/LandingPage"));
const TeamPanel = React.lazy(() => import("../components/TeamPanel"));
const QueryLibrary = React.lazy(() => import("../components/QueryLibrary"));
const AuthForm = React.lazy(() => import("../components/auth/AuthForm"));
const SettingsPanel = React.lazy(
  () => import("../components/settings/SettingsPanel")
);
const ProfileSettings = React.lazy(
  () => import("../components/ProfileSettings")
);
const SecurityAuditLog = React.lazy(
  () => import("../components/SecurityAuditLog")
);
const QueryHistory = React.lazy(() => import("../components/QueryHistory"));
const DataSourceExplorer = React.lazy(
  () => import("../components/DataSourceExplorer")
);
const QueryScheduler = React.lazy(() => import("../components/QueryScheduler"));
const DatabaseExplorer = React.lazy(
  () => import("../components/DatabaseExplorer")
);

interface AppRoutesProps {
  user: any;
  sqlQuery: string;
  isLoading: boolean;
  queryResult: any;
  selectedConnection: any;
  connections: any[];
  performanceMetrics: any;
  queryHistory: any[];
  savedQueries: any[];
  teamMembers: any[];
  handleQueryChange: (sql: string) => void;
  handleSaveQuery: () => void;
  handleRunQuery: (sqlToExecute?: string) => void;
  executeQuery: (sql: string) => void;
  setSqlQuery: (v: string) => void;
  setNaturalQuery: (v: string) => void;
  setSelectedConnection: (v: any) => void;
  setConnections: (v: any[]) => void;
  setActiveConnectionId: (v: string | null) => void;
  resultsRef: React.RefObject<HTMLDivElement>;
  handleAddConnection: (connection: any) => void;
  handleInviteMember: (
    email: string,
    role: "admin" | "editor" | "viewer"
  ) => void;
  handleUpdateMemberRole: (
    id: string,
    role: "admin" | "editor" | "viewer"
  ) => void;
  handleSelectSavedQuery: (query: any) => void;
  handleToggleQueryFavorite: (id: string) => void;
  isAdmin: boolean;
  handleSelectQueryHistoryItem: (item: any) => void;
}

const AppRoutes: React.FC<AppRoutesProps> = ({
  user,
  sqlQuery,
  isLoading,
  queryResult,
  selectedConnection,
  connections,
  performanceMetrics,
  queryHistory,
  savedQueries,
  teamMembers,
  handleQueryChange,
  handleSaveQuery,
  handleRunQuery,
  executeQuery,
  setSqlQuery,
  setNaturalQuery,
  setSelectedConnection,
  setConnections,
  setActiveConnectionId,
  resultsRef,
  handleAddConnection,
  handleInviteMember,
  handleUpdateMemberRole,
  handleSelectSavedQuery,
  handleToggleQueryFavorite,
  handleSelectQueryHistoryItem,
  isAdmin,
}) => {
  const navigate = useNavigate();

  // Redirect component for authenticated users
  const AuthenticatedRedirect = ({
    to,
    message,
  }: {
    to: string;
    message: string;
  }) => {
    useEffect(() => {
      const timer = setTimeout(() => navigate(to), 1500);
      return () => clearTimeout(timer);
    }, [to]);

    return (
      <div className="text-center py-16">
        <h2 className="text-2xl font-semibold mb-4">Welcome back!</h2>
        <p className="text-gray-400 mb-6">{message}</p>
      </div>
    );
  };

  return (
    <Suspense fallback={<LoadingScreen type="data" />}>
      <Routes>
        {/* Landing Page Route - Default route */}
        <Route
          path="/"
          element={
            user ? (
              // If user is authenticated, redirect to query page
              <AuthenticatedRedirect
                to="/query"
                message="Redirecting you to the query editor..."
              />
            ) : (
              <LandingPage
                onLogin={() => navigate("/login")}
                user={user}
                onGetStarted={() =>
                  user ? navigate("/query") : navigate("/login")
                }
              />
            )
          }
        />

        {/* Authentication Routes */}
        <Route
          path="/login"
          element={
            user ? (
              // If user is already authenticated, redirect to query
              <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 text-white flex items-center justify-center">
                <AuthenticatedRedirect
                  to="/query"
                  message="You are already authenticated. Redirecting to query editor..."
                />
              </div>
            ) : (
              <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 text-white flex items-center justify-center">
                <AuthForm
                  mode="signin"
                  onSuccess={() => {
                    // Navigate immediately since refreshUser should have updated the state
                    setTimeout(() => {
                      navigate("/query");
                    }, 100);
                  }}
                  onModeChange={(
                    newMode: "signin" | "signup" | "forgot" | "forgot-confirm"
                  ) => {
                    if (newMode === "signup") navigate("/signup");
                    if (newMode === "forgot") navigate("/forgot-password");
                  }}
                />
              </div>
            )
          }
        />

        <Route
          path="/signup"
          element={
            user ? (
              // If user is already authenticated, redirect to query
              <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 text-white flex items-center justify-center">
                <AuthenticatedRedirect
                  to="/query"
                  message="You are already authenticated. Redirecting to query editor..."
                />
              </div>
            ) : (
              <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 text-white flex items-center justify-center">
                <AuthForm
                  mode="signup"
                  onSuccess={() => {
                    // Small delay to ensure auth state is updated
                    setTimeout(() => {
                      navigate("/query");
                    }, 500);
                  }}
                  onModeChange={(
                    newMode: "signin" | "signup" | "forgot" | "forgot-confirm"
                  ) => {
                    if (newMode === "signin") navigate("/login");
                    if (newMode === "forgot") navigate("/forgot-password");
                  }}
                />
              </div>
            )
          }
        />

        <Route
          path="/forgot-password"
          element={
            <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 text-white flex items-center justify-center">
              <AuthForm
                mode="forgot"
                onSuccess={() => {
                  // After password reset, redirect to login
                  navigate("/login");
                }}
                onModeChange={(
                  newMode: "signin" | "signup" | "forgot" | "forgot-confirm"
                ) => {
                  if (newMode === "signin") navigate("/login");
                  if (newMode === "signup") navigate("/signup");
                }}
              />
            </div>
          }
        />

        {/* Query Editor Route */}
        <Route
          path="/query"
          element={
            user ? (
              <div className="space-y-6">
                {selectedConnection ? (
                  <>
                    <ConnectionDetails connection={selectedConnection} />
                    {/* <div className="bg-gray-800 rounded-lg p-4">
                      <label className="block text-sm font-medium mb-2">
                        Database Connection
                      </label>
                      <select
                        value={selectedConnection?.id || ""}
                        onChange={(e) => {
                          const conn = connections
                            .filter((c) => !c.userId || c.userId === user?.uid)
                            .find((c) => c.id === e.target.value);
                          if (conn) {
                            setSelectedConnection(conn);
                            setActiveConnectionId(
                              conn.id || conn.connection_id
                            );
                            // Also update localStorage
                            localStorage.setItem(
                              "activeConnection",
                              JSON.stringify(conn)
                            );
                          }
                        }}
                        className="w-full bg-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                      >
                        {connections
                          .filter((c) => !c.userId || c.userId === user?.uid)
                          .map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name} ({c.type})
                            </option>
                          ))}
                      </select>
                    </div> */}
                    <DatabaseTables />
                    <NLPQueryInput
                      onQueryGenerated={(sql, naturalQuery) => {
                        setSqlQuery(sql);
                        setNaturalQuery(naturalQuery);
                      }}
                      onExplanationRequested={() => {}}
                    />{" "}
                    <QueryEditor
                      queryId={Date.now().toString()}
                      sqlQuery={sqlQuery}
                      initialContent={sqlQuery}
                      onQueryChange={(content) => {
                        setSqlQuery(content);
                      }}
                      onSave={async (content) => {
                        setSqlQuery(content);
                        await handleSaveQuery();
                      }}
                      onExecute={async (content) => {
                        console.log(
                          "QueryEditor onExecute called with content:",
                          content
                        );
                        setSqlQuery(content);
                        // Call executeQuery directly instead of handleRunQuery
                        await executeQuery(content);
                      }}
                      onChange={async (Content) => {
                        setSqlQuery(Content);
                        await handleSaveQuery();
                      }}
                    />
                    <div className="flex justify-end gap-3">
                      <Button
                        variant="secondary"
                        size="lg"
                        onClick={handleSaveQuery}
                        disabled={!sqlQuery.trim()}
                        icon={<Save className="w-4 h-4" />}
                      >
                        Save Query
                      </Button>
                      <Button
                        variant="primary"
                        size="lg"
                        loading={isLoading}
                        loadingText="Running..."
                        onClick={() => handleRunQuery()}
                        disabled={!sqlQuery.trim()}
                        icon={<PlayCircle className="w-4 h-4" />}
                      >
                        Run Query
                      </Button>
                    </div>
                    {performanceMetrics && (
                      <QueryPerformancePanel
                        metrics={performanceMetrics.metrics}
                        suggestions={performanceMetrics.suggestions}
                      />
                    )}
                    {(isLoading || queryResult !== null) && (
                      <div
                        ref={resultsRef}
                        className="bg-gray-800 rounded-lg p-6"
                      >
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-medium">Query Results</h3>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="secondary"
                              size="sm"
                              icon={<Download className="w-4 h-4" />}
                            >
                              Export
                            </Button>
                            <Button
                              variant="secondary"
                              size="sm"
                              icon={<Share2 className="w-4 h-4" />}
                            >
                              Share
                            </Button>
                          </div>
                        </div>
                        <div className="overflow-x-auto">
                          {isLoading ? (
                            <div className="text-gray-400 py-8 text-center">
                              Loading results...
                            </div>
                          ) : queryResult &&
                            queryResult.data &&
                            queryResult.data.length > 0 ? (
                            <>
                              <div className="mb-4 flex items-center justify-between">
                                <div className="text-sm text-gray-400">
                                  {queryResult.rowCount} rows •{" "}
                                  {queryResult.executionTime}ms
                                </div>
                                <div className="text-sm text-gray-400">
                                  {queryResult.columns?.length} columns
                                </div>
                              </div>
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b border-gray-700">
                                    {(queryResult.columns as any[])?.map(
                                      (column: any) => (
                                        <th
                                          key={column.name}
                                          className="text-left py-2 px-3 font-medium"
                                          title={`${
                                            column.database_type || column.type
                                          } - ${column.table || "table"}`}
                                        >
                                          <div className="flex flex-col">
                                            <span>{column.name}</span>
                                            <span className="text-xs text-gray-500 font-normal">
                                              {column.database_type ||
                                                column.type}
                                            </span>
                                          </div>
                                        </th>
                                      )
                                    )}
                                  </tr>
                                </thead>
                                <tbody>
                                  {queryResult.data.map(
                                    (row: any, i: number) => (
                                      <tr
                                        key={i}
                                        className="border-b border-gray-700 hover:bg-gray-700"
                                      >
                                        {(queryResult.columns as any[])?.map(
                                          (column: any, cellIdx: number) => {
                                            const value = row[column.name];
                                            return (
                                              <td
                                                key={cellIdx}
                                                className="py-2 px-3"
                                              >
                                                {value === null ||
                                                value === undefined ? (
                                                  <span className="text-gray-500 italic">
                                                    null
                                                  </span>
                                                ) : typeof value ===
                                                  "object" ? (
                                                  <span className="text-blue-400">
                                                    {JSON.stringify(value)}
                                                  </span>
                                                ) : typeof value ===
                                                  "boolean" ? (
                                                  <span
                                                    className={
                                                      value
                                                        ? "text-green-400"
                                                        : "text-red-400"
                                                    }
                                                  >
                                                    {String(value)}
                                                  </span>
                                                ) : typeof value ===
                                                  "number" ? (
                                                  <span className="text-yellow-400">
                                                    {value}
                                                  </span>
                                                ) : (
                                                  String(value)
                                                )}
                                              </td>
                                            );
                                          }
                                        )}
                                      </tr>
                                    )
                                  )}
                                </tbody>
                              </table>
                              <div className="mt-4 p-4 rounded-xl bg-gray-800 border border-gray-700 text-gray-300 shadow-sm">
                                <h3 className="text-base font-semibold mb-2 text-gray-200">
                                  Summary
                                </h3>

                                {queryResult.summary && (
                                  <p className="text-sm leading-relaxed text-gray-300">
                                    {queryResult.summary}
                                  </p>
                                )}
                              </div>
                            </>
                          ) : queryResult &&
                            queryResult.data &&
                            queryResult.data.length === 0 ? (
                            <div className="text-gray-400 py-8 text-center">
                              Query executed successfully. No rows returned.
                            </div>
                          ) : (
                            <div className="text-gray-400 py-8 text-center">
                              No results to display.
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    <QueryHistoryTabs
                      history={queryHistory}
                      onSelectQuery={handleSelectQueryHistoryItem}
                      onToggleFavorite={() => {}}
                      onCopyQuery={(sql) => navigator.clipboard.writeText(sql)}
                    />
                  </>
                ) : (
                  <div className="flex items-center justify-center h-64 bg-gray-800 rounded-lg">
                    <div className="text-center">
                      <Database className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-400 mb-4">
                        Please select a database connection to start querying
                      </p>
                      <Button
                        variant="primary"
                        onClick={() => navigate("/connections")}
                        icon={<Plus className="w-4 h-4" />}
                      >
                        Add Connection
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 bg-gray-800 rounded-lg">
                <div className="text-center">
                  <Database className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-400 mb-4">
                    Please sign in to access the Query Editor
                  </p>
                  <Button
                    variant="primary"
                    onClick={() => navigate("/login")}
                    icon={<Plus className="w-4 h-4" />}
                  >
                    Sign In
                  </Button>
                </div>
              </div>
            )
          }
        />

        {/* Dashboard Route */}
        <Route
          path="/dashboard"
          element={
            user ? (
              <DashboardGrid
                insights={[]}
                onAddInsight={() => {}}
                onRequestSuggestions={() => {}}
                onUpdateLayout={() => {}}
                onDeleteInsight={() => {}}
                onEditInsight={() => {}}
              />
            ) : (
              <div className="flex items-center justify-center h-64 bg-gray-800 rounded-lg">
                <div className="text-center">
                  <Database className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-400 mb-4">
                    Please sign in to access the Dashboard
                  </p>
                  <Button
                    variant="primary"
                    onClick={() => navigate("/login")}
                    icon={<Plus className="w-4 h-4" />}
                  >
                    Sign In
                  </Button>
                </div>
              </div>
            )
          }
        />

        {/* Visual Builder Route */}
        <Route
          path="/builder"
          element={
            user ? (
              <VisualQueryBuilder
                onQueryChange={setSqlQuery}
                selectedConnection={selectedConnection || undefined}
              />
            ) : (
              <div className="flex items-center justify-center h-64 bg-gray-800 rounded-lg">
                <div className="text-center">
                  <Database className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-400 mb-4">
                    Please sign in to access the Visual Builder
                  </p>
                  <Button
                    variant="primary"
                    onClick={() => navigate("/login")}
                    icon={<Plus className="w-4 h-4" />}
                  >
                    Sign In
                  </Button>
                </div>
              </div>
            )
          }
        />

        {/* Admin Panel Route */}
        <Route
          path="/admin"
          element={
            isAdmin ? (
              <AdminPanel onClose={() => navigate(-1)} />
            ) : (
              <div className="text-center py-16">
                <h2 className="text-2xl font-semibold text-red-400 mb-4">
                  Access Denied
                </h2>
                <p className="text-gray-400">
                  You don't have permission to access this page.
                </p>
              </div>
            )
          }
        />

        {/* Connections Management Route */}
        <Route
          path="/connections"
          element={
            <ConnectionManager
              isOpen={true}
              onSave={async (connection) => {
                handleAddConnection(connection);
              }}
              connections={connections}
              onConnect={async (connection) => {
                setSelectedConnection(connection);
                // Store in localStorage for persistence
                localStorage.setItem(
                  "activeConnection",
                  JSON.stringify(connection)
                );
              }}
              onConnectionsUpdate={(updatedConnections: any[]) => {
                setConnections(updatedConnections);
                // Store updated connections in localStorage
                localStorage.setItem(
                  "connections",
                  JSON.stringify(updatedConnections)
                );
              }}
            />
          }
        />

        {/* Query Library Route */}
        <Route
          path="/query-library"
          element={
            <QueryLibrary
              queries={savedQueries}
              onClose={() => navigate(-1)}
              onSelectQuery={handleSelectSavedQuery}
              onToggleFavorite={handleToggleQueryFavorite}
            />
          }
        />

        {/* Team Management Route */}
        <Route
          path="/team"
          element={
            <TeamPanel
              members={teamMembers}
              onClose={() => navigate(-1)}
              onInviteMember={handleInviteMember}
              onUpdateMemberRole={handleUpdateMemberRole}
            />
          }
        />

        {/* Settings Route */}
        <Route
          path="/settings"
          element={<SettingsPanel onClose={() => navigate(-1)} />}
        />

        {/* Profile Route */}
        <Route path="/profile" element={<ProfileSettings />} />

        {/* Security Route */}
        <Route path="/security" element={<SecurityAuditLog />} />

        {/* Query History Route */}
        <Route
          path="/query-history"
          element={
            <QueryHistory
              history={queryHistory}
              onSelectQuery={handleSelectQueryHistoryItem}
              onToggleFavorite={() => {}}
              onCopyQuery={(sql) => navigator.clipboard.writeText(sql)}
            />
          }
        />

        {/* Scheduled Tasks Route */}
        {/* <Route path="/scheduled-tasks" element={<ScheduledQueriesList />} /> */}

        {/* Query Scheduler Route */}
        <Route
          path="/schedule-query"
          element={
            <QueryScheduler
              queryId={Date.now().toString()}
              sql={sqlQuery}
              onClose={() => navigate("/scheduled-tasks")}
            />
          }
        />

        {/* Data Catalog Route */}
        <Route
          path="/data-catalog"
          element={
            <DataSourceExplorer
              connection={selectedConnection}
              onTableSelect={(tableName: string) => {
                setSqlQuery(`SELECT * FROM ${tableName} LIMIT 10;`);
                navigate("/query");
              }}
            />
          }
        />

        {/* Database Explorer Route */}
        <Route
          path="/database-explorer"
          element={
            <DatabaseExplorer
              onTableSelect={(database: string, table: string) => {
                setSqlQuery(`SELECT * FROM ${database}.${table} LIMIT 10;`);
                navigate("/query");
              }}
            />
          }
        />

        {/* Shared Queries Route */}
        <Route
          path="/shared-queries"
          element={
            <div className="text-center py-16">
              <h2 className="text-2xl font-semibold text-gray-300 mb-4">
                Shared Queries
              </h2>
              <p className="text-gray-400">
                Feature coming soon - View and manage shared queries with your
                team.
              </p>
            </div>
          }
        />

        {/* Comments Route */}
        <Route
          path="/comments"
          element={
            <div className="text-center py-16">
              <h2 className="text-2xl font-semibold text-gray-300 mb-4">
                Comments & Discussions
              </h2>
              <p className="text-gray-400">
                Feature coming soon - Collaborate with comments on queries and
                notebooks.
              </p>
            </div>
          }
        />

        {/* Notebooks List Route */}
        <Route
          path="/notebooks"
          element={
            <NotebookList
              userRole={user?.role || "user"}
              onOpenNotebook={(id: any) => navigate(`/notebooks/${id}`)}
            />
          }
        />

        {/* Notebook Detail Route */}
        <Route
          path="/notebooks/:id"
          element={
            <NotebookInterface
              notebookId={window.location.pathname.split("/").pop() || ""}
              userRole={user?.role || "user"}
            />
          }
        />

        {/* Mind Map Route */}
        <Route
          path="/mindmap"
          element={
            <MindMapBuilder
              selectedConnection={selectedConnection || undefined}
            />
          }
        />

        {/* Catch-all route - redirect to dashboard */}
        <Route
          path="*"
          element={
            <div className="text-center py-16">
              <h2 className="text-2xl font-semibold text-gray-300 mb-4">
                Page Not Found
              </h2>
              <p className="text-gray-400 mb-6">
                The page you're looking for doesn't exist.
              </p>
              <Button variant="primary" onClick={() => navigate("/")}>
                Go to Dashboard
              </Button>
            </div>
          }
        />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
